import React, { useState } from "react";
import UserHeader from "@/components/user/UserHeader";
import Footer from "@/components/layout/Footer";
import { Card, CardContent } from "@/components/ui/card";
import { BarChart3, PieChart, LineChart, Download, FileText, ChevronDown, ChevronUp, Info, Check, X } from "lucide-react";

const faixaEtariaOptions = [
  "18–24", "25–29", "30–34", "35–39", "40–44", "45–49", "50–54", "55–59", "60–64", "65–69", "Acima de 70", "Todas"
];
const generoOptions = ["Feminino", "Masculino", "Todos"];
const regiaoOptions = ["Sudeste", "Nordeste", "Centro-Oeste", "Sul", "Norte", "Outra"];
const situacaoOptions = ["Ativo", "Inativo", "Todos"];

// Opções para o filtro de Matrículas
const tipoMatriculaOptions = ["Ativas", "Inativas"];
const financeiroOptions = ["Pagamentos Pendentes", "Pagamentos Feitos", "Todos"];
const progressoTecnicoOptions = ["Módulo", "Disciplina", "Carga horária", "Vídeo", "Material de Apoio", "Exercícios", "Prova", "Frequência", "Todos"];

const alunosPlaceholder = [
  {
    matricula: "2024001",
    nome: "Maria Silva",
    faixaEtaria: "25–29",
    genero: "Feminino",
    regiao: "Sudeste",
    situacao: "Ativo"
  },
  {
    matricula: "2024002",
    nome: "João Souza",
    faixaEtaria: "30–34",
    genero: "Masculino",
    regiao: "Sul",
    situacao: "Inativo"
  },
  {
    matricula: "2024003",
    nome: "Ana Oliveira",
    faixaEtaria: "18–24",
    genero: "Feminino",
    regiao: "Nordeste",
    situacao: "Ativo"
  }
];

const Relatorios: React.FC = () => {
  const [faixaEtaria, setFaixaEtaria] = useState<string[]>(["Todas"]);
  const [genero, setGenero] = useState<string[]>(["Todos"]);
  const [regiao, setRegiao] = useState<string[]>([]);
  const [situacao, setSituacao] = useState<string[]>(["Todos"]);
  const [filtrosAbertos, setFiltrosAbertos] = useState(true);
  const [conclusao, setConclusao] = useState<string>("Todos");
  
  // Novos estados para filtro de Matrículas
  const [tipoMatricula, setTipoMatricula] = useState<string>("Ativas");
  const [financeiro, setFinanceiro] = useState<string[]>(["Todos"]);
  const [progressoTecnico, setProgressoTecnico] = useState<string[]>(["Todos"]);
  const [filtrosMatriculaAbertos, setFiltrosMatriculaAbertos] = useState(false);
  const [financeiroAberto, setFinanceiroAberto] = useState(false);
  const [progressoTecnicoAberto, setProgressoTecnicoAberto] = useState(false);

  // Colunas dinâmicas
  const showFaixaEtaria = faixaEtaria.length > 0 && !faixaEtaria.includes("Todas");
  const showGenero = genero.length > 0 && !genero.includes("Todos");
  const showRegiao = regiao.length > 0;
  const showSituacao = situacao.length > 0 && !situacao.includes("Todos");

  // Handlers para checkbox group
  const handleCheckboxGroup = (value: string, group: string[], setGroup: (v: string[]) => void, allValue?: string) => {
    if (allValue && value === allValue) {
      setGroup([allValue]);
    } else {
      let newGroup = group.includes(value)
        ? group.filter((v) => v !== value)
        : [...group.filter((v) => v !== allValue), value];
      if (newGroup.length === 0 && allValue) newGroup = [allValue];
      setGroup(newGroup);
    }
  };

  const handleLimparFiltros = () => {
    setFaixaEtaria(["Todas"]);
    setGenero(["Todos"]);
    setRegiao([]);
    setSituacao(["Todos"]);
    setTipoMatricula("Ativas");
    setFinanceiro(["Todos"]);
    setProgressoTecnico(["Todos"]);
  };

  // Dados de exemplo para as tabelas de conclusão
  const alunosConclusaoTodos = [
    { curso: "Gestão Ambiental", matricula: "2024001", nome: "Maria Silva", cpf: "123.456.789-00", rg: "MG-12.345.678", ch: "40h", nota: 8.5, faixaEtaria: "25–29", genero: "Feminino", regiao: "Sudeste", situacao: "Ativo", estado: "Minas Gerais", cidade: "Belo Horizonte" },
    { curso: "Educação Climática", matricula: "2024002", nome: "João Souza", cpf: "987.654.321-00", rg: "SP-98.765.432", ch: "36h", nota: 6.2, faixaEtaria: "30–34", genero: "Masculino", regiao: "Sul", situacao: "Inativo", estado: "São Paulo", cidade: "Campinas" },
    { curso: "Sustentabilidade", matricula: "2024003", nome: "Ana Oliveira", cpf: "456.789.123-00", rg: "RJ-45.678.912", ch: "40h", nota: 7.0, faixaEtaria: "18–24", genero: "Feminino", regiao: "Nordeste", situacao: "Ativo", estado: "Rio de Janeiro", cidade: "Niterói" },
    { curso: "Gestão Ambiental", matricula: "2024004", nome: "Carlos Lima", cpf: "321.654.987-00", rg: "MG-98.765.432", ch: "40h", nota: 9.2, faixaEtaria: "35–39", genero: "Masculino", regiao: "Sudeste", situacao: "Ativo", estado: "Minas Gerais", cidade: "Uberlândia" },
    { curso: "Educação Climática", matricula: "2024005", nome: "Fernanda Costa", cpf: "654.321.987-00", rg: "SP-12.345.678", ch: "38h", nota: 5.8, faixaEtaria: "40–44", genero: "Feminino", regiao: "Sul", situacao: "Inativo", estado: "São Paulo", cidade: "Santos" },
    { curso: "Sustentabilidade", matricula: "2024006", nome: "Bruno Alves", cpf: "789.123.456-00", rg: "RJ-98.765.432", ch: "40h", nota: 7.8, faixaEtaria: "45–49", genero: "Masculino", regiao: "Nordeste", situacao: "Ativo", estado: "Rio de Janeiro", cidade: "Volta Redonda" },
    { curso: "Gestão Ambiental", matricula: "2024007", nome: "Patrícia Dias", cpf: "852.963.741-00", rg: "MG-45.678.912", ch: "39h", nota: 6.9, faixaEtaria: "50–54", genero: "Feminino", regiao: "Centro-Oeste", situacao: "Ativo", estado: "Minas Gerais", cidade: "Juiz de Fora" },
    { curso: "Educação Climática", matricula: "2024008", nome: "Ricardo Martins", cpf: "963.852.741-00", rg: "SP-23.456.789", ch: "40h", nota: 8.0, faixaEtaria: "55–59", genero: "Masculino", regiao: "Norte", situacao: "Inativo", estado: "São Paulo", cidade: "São Paulo" },
    { curso: "Sustentabilidade", matricula: "2024009", nome: "Juliana Rocha", cpf: "147.258.369-00", rg: "RJ-34.567.891", ch: "40h", nota: 9.5, faixaEtaria: "60–64", genero: "Feminino", regiao: "Outra", situacao: "Ativo", estado: "Rio de Janeiro", cidade: "Petrópolis" },
    { curso: "Gestão Ambiental", matricula: "2024010", nome: "Eduardo Pires", cpf: "258.369.147-00", rg: "MG-56.789.123", ch: "35h", nota: 4.7, faixaEtaria: "65–69", genero: "Masculino", regiao: "Sudeste", situacao: "Inativo", estado: "Minas Gerais", cidade: "Contagem" },
    { curso: "Educação Climática", matricula: "2024011", nome: "Sônia Mendes", cpf: "369.147.258-00", rg: "SP-67.891.234", ch: "40h", nota: 7.1, faixaEtaria: "Acima de 70", genero: "Feminino", regiao: "Sul", situacao: "Ativo", estado: "São Paulo", cidade: "Ribeirão Preto" },
    { curso: "Gestão Ambiental", matricula: "2024012", nome: "Larissa Fictícia", cpf: "111.222.333-44", rg: "MG-11.223.344", ch: "40h", nota: 8.0, faixaEtaria: "35–39", genero: "Feminino", regiao: "Sudeste", situacao: "Ativo", estado: "Minas Gerais", cidade: "Betim" },
    { curso: "Sustentabilidade", matricula: "2024013", nome: "Pedro Ramos", cpf: "222.333.444-55", rg: "RJ-22.334.455", ch: "40h", nota: 7.5, faixaEtaria: "65–69", genero: "Masculino", regiao: "Norte", situacao: "Ativo", estado: "Rio de Janeiro", cidade: "Nova Iguaçu" },
    { curso: "Gestão Ambiental", matricula: "2024014", nome: "Aline Torres", cpf: "333.444.555-66", rg: "MG-33.445.566", ch: "40h", nota: 8.9, faixaEtaria: "Acima de 70", genero: "Feminino", regiao: "Centro-Oeste", situacao: "Inativo", estado: "Minas Gerais", cidade: "Divinópolis" },
    { curso: "Educação Climática", matricula: "2024015", nome: "Marcos Vinícius", cpf: "444.555.666-77", rg: "SP-44.556.677", ch: "40h", nota: 6.5, faixaEtaria: "50–54", genero: "Masculino", regiao: "Nordeste", situacao: "Ativo", estado: "São Paulo", cidade: "Sorocaba" },
    { curso: "Sustentabilidade", matricula: "2024016", nome: "Beatriz Faria", cpf: "555.666.777-88", rg: "RJ-55.667.788", ch: "40h", nota: 9.0, faixaEtaria: "55–59", genero: "Feminino", regiao: "Sul", situacao: "Inativo", estado: "Rio de Janeiro", cidade: "Campos dos Goytacazes" },
    { curso: "Gestão Ambiental", matricula: "2024017", nome: "Lucas Pereira", cpf: "666.777.888-99", rg: "MG-66.778.899", ch: "38h", nota: 5.0, faixaEtaria: "45–49", genero: "Masculino", regiao: "Outra", situacao: "Ativo", estado: "Minas Gerais", cidade: "Montes Claros" },
    { curso: "Educação Climática", matricula: "2024018", nome: "Camila Nunes", cpf: "777.888.999-00", rg: "SP-77.889.900", ch: "40h", nota: 7.7, faixaEtaria: "40–44", genero: "Feminino", regiao: "Sudeste", situacao: "Inativo", estado: "São Paulo", cidade: "Guarulhos" },
    { curso: "Sustentabilidade", matricula: "2024019", nome: "Rafael Lopes", cpf: "888.999.000-11", rg: "RJ-88.990.011", ch: "40h", nota: 8.2, faixaEtaria: "25–29", genero: "Masculino", regiao: "Centro-Oeste", situacao: "Ativo", estado: "Rio de Janeiro", cidade: "Duque de Caxias" },
    { curso: "Gestão Ambiental", matricula: "2024020", nome: "Patrícia Gomes", cpf: "999.000.111-22", rg: "MG-99.001.122", ch: "40h", nota: 9.8, faixaEtaria: "18–24", genero: "Feminino", regiao: "Norte", situacao: "Inativo", estado: "Minas Gerais", cidade: "Ipatinga" },
    { curso: "Educação Climática", matricula: "2024021", nome: "Tiago Barros", cpf: "000.111.222-33", rg: "SP-00.112.233", ch: "40h", nota: 6.8, faixaEtaria: "30–34", genero: "Masculino", regiao: "Sul", situacao: "Ativo", estado: "São Paulo", cidade: "São José dos Campos" },
    { curso: "Sustentabilidade", matricula: "2024022", nome: "Helena Duarte", cpf: "111.222.333-55", rg: "RJ-11.223.355", ch: "40h", nota: 7.3, faixaEtaria: "35–39", genero: "Feminino", regiao: "Nordeste", situacao: "Inativo", estado: "Rio de Janeiro", cidade: "Angra dos Reis" },
  ];
  const alunosConclusaoAprovados = alunosConclusaoTodos.filter(a => a.nota >= 7 && a.ch === "40h");
  const alunosConclusaoReprovados = alunosConclusaoTodos.filter(a => a.nota < 7 || a.ch !== "40h");

  // Dados de exemplo para matrículas ativas (financeiro)
  const dadosFinanceiros = [
    {
      nome: "Maria Silva",
      matricula: "2024001",
      curso: "Gestão Ambiental",
      situacaoFinanceira: "Pendente",
      valor: "R$ 297,00",
      dataVencimento: "15/01/2025",
      dataPagamento: "-",
      formaPagamento: "Boleto"
    },
    {
      nome: "Carlos Lima",
      matricula: "2024004",
      curso: "Gestão Ambiental",
      situacaoFinanceira: "Pago",
      valor: "R$ 297,00",
      dataVencimento: "15/12/2024",
      dataPagamento: "10/12/2024",
      formaPagamento: "PIX"
    },
    {
      nome: "Ana Oliveira",
      matricula: "2024003",
      curso: "Sustentabilidade",
      situacaoFinanceira: "Aguardando comprovante",
      valor: "R$ 297,00",
      dataVencimento: "15/01/2025",
      dataPagamento: "12/01/2025",
      formaPagamento: "Transferência"
    },
    {
      nome: "Bruno Alves",
      matricula: "2024006",
      curso: "Sustentabilidade",
      situacaoFinanceira: "Pendente",
      valor: "R$ 297,00",
      dataVencimento: "15/02/2025",
      dataPagamento: "-",
      formaPagamento: "Boleto"
    },
    {
      nome: "Patrícia Dias",
      matricula: "2024007",
      curso: "Gestão Ambiental",
      situacaoFinanceira: "Pago",
      valor: "R$ 297,00",
      dataVencimento: "15/12/2024",
      dataPagamento: "14/12/2024",
      formaPagamento: "Cartão de Crédito"
    }
  ];

  // Gerar dados fictícios de progresso técnico: 3 módulos, 8 disciplinas por módulo, para cada aluno
  const disciplinasPorModulo = [
    [
      'Introdução à Gestão Ambiental', 'Legislação Ambiental', 'Políticas Públicas', 'Gestão de Resíduos',
      'Educação Ambiental', 'Mudanças Climáticas', 'Sustentabilidade', 'Recursos Naturais'
    ],
    [
      'Gestão de Água', 'Gestão de Energia', 'Gestão de Solo', 'Gestão de Biodiversidade',
      'Gestão Urbana', 'Gestão Rural', 'Tecnologias Limpas', 'Empreendedorismo Verde'
    ],
    [
      'Projetos Ambientais', 'Monitoramento Ambiental', 'Auditoria Ambiental', 'Licenciamento Ambiental',
      'Responsabilidade Social', 'Ética Ambiental', 'Comunicação Ambiental', 'Inovação Sustentável'
    ]
  ];

  function gerarProgressoTecnicoParaAluno(aluno) {
    return disciplinasPorModulo.flatMap((disciplinas, mIdx) =>
      disciplinas.map((disciplina, dIdx) => ({
        nome: aluno.nome,
        matricula: aluno.matricula,
        modulo: `Módulo ${mIdx + 1}`,
        disciplina,
        cargaHoraria: '8h',
        video: (dIdx + mIdx) % 2 === 0 ? '✅' : '❌',
        materialApoio: (dIdx + mIdx) % 3 === 0 ? '✅' : '❌',
        exercicios: (dIdx + mIdx) % 4 === 0 ? '✅' : '❌',
        prova: (dIdx + mIdx) % 5 === 0 ? '✅' : '❌',
      }))
    );
  }

  const dadosProgressoTecnico = alunosConclusaoTodos.flatMap(gerarProgressoTecnicoParaAluno);

  // Função para filtrar dados financeiros
  const filtrarDadosFinanceiros = () => {
    if (tipoMatricula !== "Ativas") return [];
    
    let resultado = dadosFinanceiros;
    
    if (financeiro.length > 0 && !financeiro.includes("Todos")) {
      resultado = resultado.filter(item => {
        if (financeiro.includes("Pagamentos Pendentes")) {
          return item.situacaoFinanceira === "Pendente";
        }
        if (financeiro.includes("Pagamentos Feitos")) {
          return item.situacaoFinanceira === "Pago";
        }
        return true;
      });
    }
    
    return resultado;
  };

  // Função para filtrar dados de progresso técnico
  const filtrarDadosProgressoTecnico = () => {
    if (tipoMatricula !== "Ativas") return [];
    
    return dadosProgressoTecnico;
  };

  const dadosFinanceirosFiltrados = filtrarDadosFinanceiros();
  const dadosProgressoTecnicoFiltrados = filtrarDadosProgressoTecnico().filter(item => item.nome === 'Aline Torres');
  const dadosProgressoTecnicoFiltradosOrdenados = dadosProgressoTecnicoFiltrados.slice().sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'));

  // Ordenar os dados financeiros por nome antes de exibir
  const dadosFinanceirosFiltradosOrdenados = dadosFinanceirosFiltrados.slice().sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'));

  // Função para filtrar alunos conforme filtros de perfil (para relatório de todos os alunos)
  function filtrarAlunosPerfil() {
    let resultado = alunosConclusaoTodos;
    // Faixa Etária
    if (faixaEtaria.length > 0 && !faixaEtaria.includes("Todas")) {
      resultado = resultado.filter(a => faixaEtaria.includes(a.faixaEtaria));
    }
    // Gênero
    if (genero.length > 0 && !genero.includes("Todos")) {
      resultado = resultado.filter(a => genero.includes(a.genero));
    }
    // Região
    if (regiao.length > 0) {
      resultado = resultado.filter(a => regiao.includes(a.regiao));
    }
    // Situação
    if (situacao.length > 0 && !situacao.includes("Todos")) {
      resultado = resultado.filter(a => situacao.includes(a.situacao));
    }
    // Ordenar por nome
    resultado = resultado.slice().sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'));
    return resultado;
  }
  // Função para filtrar alunos conforme conclusão (para relatórios de aprovados/reprovados)
  function filtrarAlunosConclusao() {
    let resultado = alunosConclusaoTodos;
    if (conclusao === "Aprovados") {
      resultado = resultado.filter(a => a.nota >= 7 && a.ch === "40h");
    } else if (conclusao === "Reprovados") {
      resultado = resultado.filter(a => a.nota < 7 || a.ch !== "40h");
    }
    return resultado;
  }
  const alunosFiltradosPerfil = filtrarAlunosPerfil();
  const alunosFiltradosConclusao = filtrarAlunosConclusao();

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <UserHeader />
      <div className="flex-grow bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
        <div className="w-full mx-auto">
          <div className="flex items-center mb-6">
            <h1 className="text-2xl font-bold text-gray-900">Relatórios</h1>
          </div>

          {/* Filtros de Perfil do Aluno (Retrátil) */}
          <div className="bg-white p-6 rounded-lg shadow mb-6">
            <div className="flex items-center justify-between cursor-pointer select-none" onClick={() => setFiltrosAbertos(v => !v)}>
              <h2 className="text-lg font-bold text-[#0e7cb4] mb-0">Perfil do Aluno</h2>
              {filtrosAbertos ? <ChevronUp className="w-5 h-5 text-[#0e7cb4]" /> : <ChevronDown className="w-5 h-5 text-[#0e7cb4]" />}
            </div>
            {filtrosAbertos && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-4 mt-4">
                  {/* Faixa Etária */}
                  <div>
                    <label className="block font-medium mb-2">Faixa Etária</label>
                    <div className="flex flex-col gap-1">
                      {faixaEtariaOptions.map(opt => (
                        <label key={opt} className="inline-flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={faixaEtaria.includes(opt)}
                            onChange={() => handleCheckboxGroup(opt, faixaEtaria, setFaixaEtaria, "Todas")}
                          />
                          <span>{opt}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  {/* Gênero */}
                  <div>
                    <label className="block font-medium mb-2">Gênero</label>
                    <div className="flex flex-col gap-1">
                      {generoOptions.map(opt => (
                        <label key={opt} className="inline-flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={genero.includes(opt)}
                            onChange={() => handleCheckboxGroup(opt, genero, setGenero, "Todos")}
                          />
                          <span>{opt}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  {/* Região */}
                  <div>
                    <label className="block font-medium mb-2">Região</label>
                    <div className="flex flex-col gap-1">
                      {regiaoOptions.map(opt => (
                        <label key={opt} className="inline-flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={regiao.includes(opt)}
                            onChange={() => handleCheckboxGroup(opt, regiao, setRegiao)}
                          />
                          <span>{opt}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  {/* Situação */}
                  <div>
                    <label className="block font-medium mb-2">Situação</label>
                    <div className="flex flex-col gap-1">
                      {situacaoOptions.map(opt => (
                        <label key={opt} className="inline-flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={situacao.includes(opt)}
                            onChange={() => handleCheckboxGroup(opt, situacao, setSituacao, "Todos")}
                          />
                          <span>{opt}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
                {/* Botões de ação */}
                <div className="flex flex-wrap gap-2 mt-4">
                  <button className="bg-gray-200 text-gray-800 px-4 py-2 rounded font-medium" onClick={handleLimparFiltros}>Limpar Filtros</button>
                </div>
              </>
            )}
          </div>

          {/* Filtro de Conclusão */}
          <div className="bg-white p-6 rounded-lg shadow mb-6">
            <label className="block font-bold text-[#0e7cb4] mb-2">Conclusão</label>
            <div className="flex gap-6 mb-2">
              {["Todos", "Aprovados", "Reprovados"].map(opt => (
                <label key={opt} className="inline-flex items-center gap-2">
                  <input
                    type="radio"
                    name="conclusao"
                    value={opt}
                    checked={conclusao === opt}
                    onChange={() => setConclusao(opt)}
                  />
                  <span>{opt}</span>
                </label>
              ))}
            </div>
            <div className="flex flex-wrap gap-2 mt-4">
              <button className="bg-gray-200 text-gray-800 px-4 py-2 rounded font-medium" onClick={handleLimparFiltros}>Limpar Filtros</button>
            </div>
          </div>

          {/* Filtro de Matrículas */}
          <div className="bg-white p-6 rounded-lg shadow mb-6">
            <div className="flex items-center justify-between cursor-pointer select-none" onClick={() => setFiltrosMatriculaAbertos(v => !v)}>
              <h2 className="text-lg font-bold text-[#0e7cb4] mb-0">Filtro: Matrículas</h2>
              {filtrosMatriculaAbertos ? <ChevronUp className="w-5 h-5 text-[#0e7cb4]" /> : <ChevronDown className="w-5 h-5 text-[#0e7cb4]" />}
            </div>
            {filtrosMatriculaAbertos && (
              <>
                {/* Radio group Ativas/Inativas */}
                <div className="flex gap-6 mt-4 mb-6">
                  {tipoMatriculaOptions.map(opt => (
                    <label key={opt} className="inline-flex items-center gap-2">
                      <input
                        type="radio"
                        name="tipoMatricula"
                        value={opt}
                        checked={tipoMatricula === opt}
                        onChange={() => setTipoMatricula(opt)}
                      />
                      <span>{opt}</span>
                    </label>
                  ))}
                </div>
                {/* Subfiltros Financeiro e Progresso Técnico só se Ativas estiver selecionado */}
                {tipoMatricula === "Ativas" && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Subfiltro Financeiro */}
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="flex items-center justify-between cursor-pointer select-none mb-3" onClick={() => setFinanceiroAberto(v => !v)}>
                        <h3 className="font-bold text-[#0e7cb4]">Financeiro</h3>
                        {financeiroAberto ? <ChevronUp className="w-4 h-4 text-[#0e7cb4]" /> : <ChevronDown className="w-4 h-4 text-[#0e7cb4]" />}
                      </div>
                      {financeiroAberto && (
                        <div className="flex flex-col gap-2">
                          {financeiroOptions.map(opt => (
                            <label key={opt} className="inline-flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={financeiro.includes(opt)}
                                onChange={() => handleCheckboxGroup(opt, financeiro, setFinanceiro, "Todos")}
                              />
                              <span>{opt}</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Subfiltro Progresso Técnico */}
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="flex items-center justify-between cursor-pointer select-none mb-3" onClick={() => setProgressoTecnicoAberto(v => !v)}>
                        <h3 className="font-bold text-[#0e7cb4]">Progresso Técnico</h3>
                        {progressoTecnicoAberto ? <ChevronUp className="w-4 h-4 text-[#0e7cb4]" /> : <ChevronDown className="w-4 h-4 text-[#0e7cb4]" />}
                      </div>
                      {progressoTecnicoAberto && (
                        <div className="flex flex-col gap-2">
                          {progressoTecnicoOptions.map(opt => (
                            <label key={opt} className="inline-flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={progressoTecnico.includes(opt)}
                                onChange={() => handleCheckboxGroup(opt, progressoTecnico, setProgressoTecnico, "Todos")}
                              />
                              <span>{opt}</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                <div className="flex flex-wrap gap-2 mt-4">
                  <button className="bg-gray-200 text-gray-800 px-4 py-2 rounded font-medium" onClick={handleLimparFiltros}>Limpar Filtros</button>
                </div>
              </>
            )}
          </div>

          {/* Tabelas de Resultados por Conclusão */}
          {(conclusao === "Todos") && (
            <div className="bg-white p-6 rounded-lg shadow mb-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-[#0e7cb4] mb-0">Relatório de Todos os Alunos</h2>
                <span className="text-sm text-gray-600">{alunosFiltradosPerfil.length} resultado{alunosFiltradosPerfil.length === 1 ? '' : 's'}</span>
              </div>
              <p className="mb-2 text-gray-600">Exibe todos os alunos, independentemente do status de aprovação, conforme os filtros selecionados.</p>
              <div>
                <table className="min-w-full text-sm text-left">
                  <thead>
                    <tr>
                      <th className="py-2 px-3 whitespace-nowrap">Matrícula</th>
                      <th className="py-2 px-3 whitespace-nowrap">Nome</th>
                      <th className="py-2 px-3 whitespace-nowrap">RG</th>
                      <th className="py-2 px-3 whitespace-nowrap">CPF</th>
                      <th className="py-2 px-3 whitespace-nowrap">Faixa Etária</th>
                      <th className="py-2 px-3 whitespace-nowrap">Gênero</th>
                      <th className="py-2 px-3 whitespace-nowrap">Região</th>
                      <th className="py-2 px-3 whitespace-nowrap">Estado</th>
                      <th className="py-2 px-3 whitespace-nowrap">Cidade</th>
                      <th className="py-2 px-3 whitespace-nowrap">Situação</th>
                    </tr>
                  </thead>
                  <tbody>
                    {alunosFiltradosPerfil.map((aluno, idx) => (
                      <tr key={aluno.matricula} className="border-b">
                        <td className="py-2 px-3 whitespace-nowrap">{aluno.matricula}</td>
                        <td className="py-2 px-3 whitespace-nowrap">{aluno.nome}</td>
                        <td className="py-2 px-3 whitespace-nowrap">{aluno.rg}</td>
                        <td className="py-2 px-3 whitespace-nowrap">{aluno.cpf}</td>
                        <td className="py-2 px-3 whitespace-nowrap">{aluno.faixaEtaria}</td>
                        <td className="py-2 px-3 whitespace-nowrap">{aluno.genero}</td>
                        <td className="py-2 px-3 whitespace-nowrap">{aluno.regiao}</td>
                        <td className="py-2 px-3 whitespace-nowrap">{aluno.estado}</td>
                        <td className="py-2 px-3 whitespace-nowrap">{aluno.cidade}</td>
                        <td className="py-2 px-3 whitespace-nowrap">{aluno.situacao}</td>
                      </tr>
                    ))}
                    {alunosFiltradosPerfil.length === 0 && (
                      <tr>
                        <td colSpan={10} className="py-6 px-3 text-center">
                          <div className="inline-flex items-center gap-2 text-[#b40e0e] bg-red-50 border border-red-200 rounded px-4 py-2 font-medium">
                            <X className="w-5 h-5 text-[#b40e0e]" />
                            Nenhum aluno encontrado para os filtros selecionados. Tente remover alguns filtros.
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {/* Botões de exportação */}
              <div className="flex flex-wrap gap-2 mt-6">
                <button className="border border-[#0e7cb4] text-[#0e7cb4] px-4 py-2 rounded font-medium bg-white">Exportar para PDF</button>
              </div>
            </div>
          )}
          {(conclusao === "Aprovados") && (
            <div className="bg-white p-6 rounded-lg shadow mb-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-[#0e7cb4] mb-0">Relatório de Alunos Aprovados</h2>
                <span className="text-sm text-gray-600">{alunosFiltradosConclusao.length} resultado{alunosFiltradosConclusao.length === 1 ? '' : 's'}</span>
              </div>
              <p className="mb-2 text-gray-600">Exibe apenas alunos com nota maior ou igual a 7 e carga horária igual ou superior à do curso.</p>
              <div>
                <table className="min-w-full text-sm text-left">
                  <thead>
                    <tr>
                      <th className="py-2 px-3 whitespace-nowrap">Matrícula</th>
                      <th className="py-2 px-3 whitespace-nowrap">Nome</th>
                      <th className="py-2 px-3 whitespace-nowrap">CPF</th>
                      <th className="py-2 px-3 whitespace-nowrap">RG</th>
                      <th className="py-2 px-3 whitespace-nowrap">C.H.</th>
                      <th className="py-2 px-3 whitespace-nowrap">Nota</th>
                    </tr>
                  </thead>
                  <tbody>
                    {alunosFiltradosConclusao.map((aluno, idx) => (
                      <tr key={aluno.matricula} className="border-b">
                        <td className="py-2 px-3 whitespace-nowrap">{aluno.matricula}</td>
                        <td className="py-2 px-3 whitespace-nowrap">{aluno.nome}</td>
                        <td className="py-2 px-3 whitespace-nowrap">{aluno.cpf}</td>
                        <td className="py-2 px-3 whitespace-nowrap">{aluno.rg}</td>
                        <td className="py-2 px-3 whitespace-nowrap">360h</td>
                        <td className="py-2 px-3 whitespace-nowrap">{aluno.nota}</td>
                      </tr>
                    ))}
                    {alunosFiltradosConclusao.length === 0 && (
                      <tr>
                        <td colSpan={6} className="py-6 px-3 text-center">
                          <div className="inline-flex items-center gap-2 text-[#b40e0e] bg-red-50 border border-red-200 rounded px-4 py-2 font-medium">
                            <X className="w-5 h-5 text-[#b40e0e]" />
                            Nenhum aluno encontrado para os filtros selecionados. Tente remover alguns filtros.
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {/* Botões de exportação */}
              <div className="flex flex-wrap gap-2 mt-6">
                <button className="border border-[#0e7cb4] text-[#0e7cb4] px-4 py-2 rounded font-medium bg-white">Exportar para PDF</button>
              </div>
            </div>
          )}
          {(conclusao === "Reprovados") && (
            <div className="bg-white p-6 rounded-lg shadow mb-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-[#0e7cb4] mb-0">Relatório de Alunos Reprovados</h2>
                <span className="text-sm text-gray-600">{alunosFiltradosConclusao.length} resultado{alunosFiltradosConclusao.length === 1 ? '' : 's'}</span>
              </div>
              <p className="mb-2 text-gray-600">Exibe apenas alunos com nota menor que 7 ou carga horária inferior à do curso.</p>
              <div>
                <table className="min-w-full text-sm text-left">
                  <thead>
                    <tr>
                      <th className="py-2 px-3 whitespace-nowrap">Matrícula</th>
                      <th className="py-2 px-3 whitespace-nowrap">Nome</th>
                      <th className="py-2 px-3 whitespace-nowrap">CPF</th>
                      <th className="py-2 px-3 whitespace-nowrap">RG</th>
                      <th className="py-2 px-3 whitespace-nowrap">C.H.</th>
                      <th className="py-2 px-3 whitespace-nowrap">Nota</th>
                    </tr>
                  </thead>
                  <tbody>
                    {alunosFiltradosConclusao.map((aluno, idx) => (
                      <tr key={aluno.matricula} className="border-b">
                        <td className="py-2 px-3 whitespace-nowrap">{aluno.matricula}</td>
                        <td className="py-2 px-3 whitespace-nowrap">{aluno.nome}</td>
                        <td className="py-2 px-3 whitespace-nowrap">{aluno.cpf}</td>
                        <td className="py-2 px-3 whitespace-nowrap">{aluno.rg}</td>
                        <td className="py-2 px-3 whitespace-nowrap">360h</td>
                        <td className="py-2 px-3 whitespace-nowrap">{aluno.nota}</td>
                      </tr>
                    ))}
                    {alunosFiltradosConclusao.length === 0 && (
                      <tr>
                        <td colSpan={6} className="py-6 px-3 text-center">
                          <div className="inline-flex items-center gap-2 text-[#b40e0e] bg-red-50 border border-red-200 rounded px-4 py-2 font-medium">
                            <X className="w-5 h-5 text-[#b40e0e]" />
                            Nenhum aluno encontrado para os filtros selecionados. Tente remover alguns filtros.
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {/* Botões de exportação */}
              <div className="flex flex-wrap gap-2 mt-6">
                <button className="border border-[#0e7cb4] text-[#0e7cb4] px-4 py-2 rounded font-medium bg-white">Exportar para PDF</button>
              </div>
            </div>
          )}

          {/* Tabelas de Resultados para Matrículas Ativas */}
          {tipoMatricula === "Ativas" && (
            <>
              {/* Tabela de Dados Financeiros */}
              {(financeiro.length > 0 || financeiroAberto) && (
                <div className="bg-white p-6 rounded-lg shadow mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-bold text-[#0e7cb4] mb-0">🧾 Relatório Financeiro - Matrículas Ativas</h2>
                    <span className="text-sm text-gray-600">{dadosFinanceirosFiltradosOrdenados.length} resultado{dadosFinanceirosFiltradosOrdenados.length === 1 ? '' : 's'}</span>
                  </div>
                  <p className="mb-2 text-gray-600">Dados financeiros dos alunos com matrículas ativas, conforme os filtros selecionados.</p>
                  <div>
                    <table className="min-w-full text-sm text-left">
                      <thead>
                        <tr>
                          <th className="py-2 px-3 whitespace-nowrap">Matrícula</th>
                          <th className="py-2 px-3 whitespace-nowrap">Nome</th>
                          <th className="py-2 px-3 whitespace-nowrap">Status</th>
                          <th className="py-2 px-3 whitespace-nowrap">Situação</th>
                          <th className="py-2 px-3 whitespace-nowrap">Valor</th>
                          <th className="py-2 px-3 whitespace-nowrap">Data de Vencimento</th>
                          <th className="py-2 px-3 whitespace-nowrap">Data do Pagamento</th>
                          <th className="py-2 px-3 whitespace-nowrap">Forma de Pagamento</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dadosFinanceirosFiltradosOrdenados.map((item, idx) => (
                          <tr key={item.matricula} className="border-b">
                            <td className="py-2 px-3 whitespace-nowrap">{item.matricula}</td>
                            <td className="py-2 px-3 whitespace-nowrap">{item.nome}</td>
                            <td className="py-2 px-3 whitespace-nowrap">{(() => {
                              const aluno = alunosConclusaoTodos.find(a => a.matricula === item.matricula);
                              return aluno ? aluno.situacao : '-';
                            })()}</td>
                            <td className="py-2 px-3 whitespace-nowrap">
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                (item.situacaoFinanceira === "Pago" || item.situacaoFinanceira === "Aguardando comprovante") ? "bg-green-100 text-green-800" :
                                item.situacaoFinanceira === "Pendente" ? "bg-red-100 text-red-800" :
                                "bg-yellow-100 text-yellow-800"
                              }`}>
                                {item.situacaoFinanceira === "Aguardando comprovante" ? "Pago" : item.situacaoFinanceira}
                              </span>
                            </td>
                            <td className="py-2 px-3 whitespace-nowrap">{item.valor}</td>
                            <td className="py-2 px-3 whitespace-nowrap">{item.dataVencimento}</td>
                            <td className="py-2 px-3 whitespace-nowrap">{item.dataPagamento}</td>
                            <td className="py-2 px-3 whitespace-nowrap">{item.formaPagamento}</td>
                          </tr>
                        ))}
                        {dadosFinanceirosFiltradosOrdenados.length === 0 && (
                          <tr>
                            <td colSpan={8} className="py-6 px-3 text-center">
                              <div className="inline-flex items-center gap-2 text-[#b40e0e] bg-red-50 border border-red-200 rounded px-4 py-2 font-medium">
                                <X className="w-5 h-5 text-[#b40e0e]" />
                                Nenhum dado financeiro encontrado para os filtros selecionados. Tente remover alguns filtros.
                              </div>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  {/* Botões de exportação */}
                  <div className="flex flex-wrap gap-2 mt-6">
                    <button className="border border-[#0e7cb4] text-[#0e7cb4] px-4 py-2 rounded font-medium bg-white">Exportar para PDF</button>
                  </div>
                </div>
              )}

              {/* Tabela de Progresso Técnico */}
              {(progressoTecnico.length > 0 || progressoTecnicoAberto) && (
                <div className="bg-white p-6 rounded-lg shadow mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-bold text-[#0e7cb4] mb-0">🎓 Relatório de Progresso Técnico - Matrículas Ativas</h2>
                    <span className="text-sm text-gray-600">{dadosProgressoTecnicoFiltradosOrdenados.length} resultado{dadosProgressoTecnicoFiltradosOrdenados.length === 1 ? '' : 's'}</span>
                  </div>
                  <p className="mb-2 text-gray-600">Progresso acadêmico dos alunos com matrículas ativas, conforme os filtros selecionados.</p>
                  <div className="mb-4 p-3 bg-gray-50 rounded-lg text-sm">
                    <p className="font-medium mb-1">Legenda dos ícones:</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                      <span><Check className="inline w-4 h-4 text-[#0e7cb4] mb-0.5" /> = Acessou/Realizou</span>
                      <span><X className="inline w-4 h-4 text-red-600 mb-0.5" /> = Não acessou/Realizou</span>
                      <span><span className="bg-green-100 text-green-800 px-1 rounded">90%+</span> = Frequência Excelente</span>
                      <span><span className="bg-yellow-100 text-yellow-800 px-1 rounded">75-89%</span> = Frequência Boa</span>
                    </div>
                  </div>
                  <table className="min-w-full text-sm text-left">
                    <thead>
                      <tr>
                        <th className="py-2 px-3 whitespace-nowrap" rowSpan={2}>Matrícula</th>
                        <th className="py-2 px-3 whitespace-nowrap" rowSpan={2}>Nome</th>
                        <th className="py-2 px-3 whitespace-nowrap" rowSpan={2}>Módulo</th>
                        <th className="py-2 px-3 whitespace-nowrap" rowSpan={2}>Disciplina</th>
                        <th className="py-2 px-3 whitespace-nowrap" rowSpan={2}>Vídeo<Info className="inline w-3 h-3 ml-1 text-gray-400" /></th>
                        <th className="py-1 px-3 whitespace-nowrap text-center" colSpan={1}>Material complementar</th>
                        <th className="py-2 px-3 whitespace-nowrap" rowSpan={2}>Exercícios<Info className="inline w-3 h-3 ml-1 text-gray-400" /></th>
                        <th className="py-2 px-3 whitespace-nowrap" rowSpan={2}>Prova<Info className="inline w-3 h-3 ml-1 text-gray-400" /></th>
                        <th className="py-2 px-3 whitespace-nowrap" rowSpan={2}>Carga Horária</th>
                        <th className="py-2 px-3 whitespace-nowrap" rowSpan={2}>Frequência (%)</th>
                      </tr>
                      <tr>
                        <th className="py-1 px-3 whitespace-nowrap text-center">Texto/links</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dadosProgressoTecnicoFiltradosOrdenados.map((item, idx) => {
                        const atividades = [item.video, item.materialApoio, item.exercicios, item.prova];
                        const realizadas = atividades.filter(a => a === '✅').length;
                        const media = realizadas * 25;
                        return (
                          <tr key={item.matricula} className="border-b">
                            <td className="py-2 px-3 whitespace-nowrap">{item.matricula}</td>
                            <td className="py-2 px-3 whitespace-nowrap">{item.nome}</td>
                            <td className="py-2 px-3 whitespace-nowrap">{item.modulo}</td>
                            <td className="py-2 px-3 whitespace-nowrap">{item.disciplina}</td>
                            <td className="py-2 px-3 whitespace-nowrap">
                              <span className={item.video === "✅" ? "text-[#0e7cb4]" : "text-red-600"}>
                                {item.video === "✅" ? <Check className="inline w-5 h-5 text-[#0e7cb4]" /> : <X className="inline w-5 h-5 text-red-600" />}
                              </span>
                            </td>
                            <td className="py-2 px-3 whitespace-nowrap text-center">
                              <span className={item.materialApoio === "✅" ? "text-[#0e7cb4]" : "text-red-600"}>
                                {item.materialApoio === "✅" ? <Check className="inline w-5 h-5 text-[#0e7cb4]" /> : <X className="inline w-5 h-5 text-red-600" />}
                              </span>
                            </td>
                            <td className="py-2 px-3 whitespace-nowrap">{[7.5, 8.0, 6.8, 9.2, 7.0, 8.7, 6.5, 7.8, 8.3, 7.1][idx % 10]}</td>
                            <td className="py-2 px-3 whitespace-nowrap">{[8.0, 7.2, 6.5, 9.0, 7.8, 8.5, 7.0, 8.1, 7.6, 6.9][idx % 10]}</td>
                            <td className="py-2 px-3 whitespace-nowrap">8h</td>
                            <td className={`py-2 px-3 whitespace-nowrap`}>
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                media >= 90 ? "bg-green-100 text-green-800" :
                                media >= 75 ? "bg-yellow-100 text-yellow-800" :
                                "bg-red-100 text-red-800"
                              }`}>
                                {media}%
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                      {dadosProgressoTecnicoFiltradosOrdenados.length === 0 && (
                        <tr>
                          <td colSpan={11} className="py-6 px-3 text-center">
                            <div className="inline-flex items-center gap-2 text-[#b40e0e] bg-red-50 border border-red-200 rounded px-4 py-2 font-medium">
                              <X className="w-5 h-5 text-[#b40e0e]" />
                              Nenhum dado de progresso encontrado para os filtros selecionados. Tente remover alguns filtros.
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                  {/* Botões de exportação */}
                  <div className="flex flex-wrap gap-2 mt-6">
                    <button className="border border-[#0e7cb4] text-[#0e7cb4] px-4 py-2 rounded font-medium bg-white">Exportar para PDF</button>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Relatórios de Gestão */}
          {/* --- REMOVIDO: Relatórios de Gestão e cards de relatórios --- */}
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default Relatorios; 